import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Header } from "../../Header";
import { useLocation } from "react-router-dom";
import './HorecaRateReport.css';
import { local_url, local_signage } from "../../Const";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

export const HorecaDayRate = () => {
  const location = useLocation();
  
  // State variables for filters
  const [sfAreaId, setSfAreaId] = useState("");
  const [channel] = useState("HORECA"); // Default to HORECA
  const [businessDate, setBusinessDate] = useState(new Date());

  // State variables for data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [filterText, setFilterText] = useState('');

  // State variables for dropdowns
  const [sfAreas, setSfAreas] = useState([]);

  // "Customer Rate" toggle — when checked, shows a Customer dropdown
  // (populated for the selected area) instead of the fixed Channel
  // dropdown. Picking a customer then loads their rate report. Backing
  // API for the customer list is not available yet, so this stays empty
  // until that endpoint is provided.
  const [showCustomerRate, setShowCustomerRate] = useState(false);
  const [customerList, setCustomerList] = useState([]);
  const [loadingCustomerList, setLoadingCustomerList] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');

  // Rate-details rows for the selected customer(s) — shown directly in the
  // main table once a customer is picked, no separate popup.
  const [rateDetailsRows, setRateDetailsRows] = useState([]);
  const [loadingRateDetails, setLoadingRateDetails] = useState(false);
  const [isExportingCustomerRate, setIsExportingCustomerRate] = useState(false);
  const [hasLoadedRateDetails, setHasLoadedRateDetails] = useState(false);

  // Get authentication token and user ID from localStorage
  const token = localStorage.getItem('AuthorizationToken');
  const userId = localStorage.getItem('userId');

  // Handle authentication error
  const handleAuthError = () => {
    setError("Authentication error. Please log in again.");
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/login";
  };

  // Format date for API calls (YYYYMMDD format)
  const formatDateForApi = (date) => {
    if (!date) return "";
    
    let formattedDate;
    
    if (typeof date === 'string') {
      formattedDate = new Date(date);
    } else {
      formattedDate = date;
    }
    
    if (formattedDate instanceof Date && !isNaN(formattedDate.getTime())) {
      const year = formattedDate.getFullYear();
      const month = String(formattedDate.getMonth() + 1).padStart(2, '0');
      const day = String(formattedDate.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    }
    
    return "";
  };

  // Format date for display (DD-MM-YYYY)
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "";
    
    try {
      const date = new Date(dateString);
      
      // Check if date is valid and not the SQL min date
      if (isNaN(date.getTime()) || 
          dateString === "1723-01-01T00:00:00" || 
          dateString === "1753-01-01T00:00:00" ||
          dateString.includes("0001-01-01")) {
        return ""; 
      }
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}-${month}-${year}`;
    } catch (e) {
      console.error("Error formatting date:", e);
      return "";
    }
  };

  // Handle business date change
  const handleDateChange = (newDate) => {
    setBusinessDate(newDate ? newDate.toDate() : new Date());
  };

  // Handle area change
  const handleAreaChange = (areaId) => {
    setSfAreaId(areaId);
    setSelectedCustomerId('');
    setRateDetailsRows([]);
    setHasLoadedRateDetails(false);
    if (showCustomerRate) {
      fetchCustomerList(areaId);
    }
  };

  const fetchCustomerList = async (areaId) => {
    if (!areaId) return;
    setLoadingCustomerList(true);
    try {
      const response = await fetch(
        `${local_signage}/api/v1/fetchrebateschemecustomerbyarea?AreaId=${areaId}&PricingSchemeType=HORECA`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Failed to fetch customer list');
      const data = await response.json();
      setCustomerList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching customer list:', err);
      setCustomerList([]);
    } finally {
      setLoadingCustomerList(false);
    }
  };

  const handleCustomerRateToggle = (checked) => {
    setShowCustomerRate(checked);
    setSelectedCustomerId('');
    setRateDetailsRows([]);
    setHasLoadedRateDetails(false);
    if (checked && sfAreaId) {
      fetchCustomerList(sfAreaId);
    } else {
      setCustomerList([]);
    }
  };

  // Formats a YYYYMMDD string (e.g. "20260805") as DD-MM-YYYY.
  const formatYyyymmdd = (value) => {
    if (!value) return '-';
    const str = String(value);
    if (str.length !== 8) return str;
    return `${str.slice(6, 8)}-${str.slice(4, 6)}-${str.slice(0, 4)}`;
  };

  const fetchOneCustomerRateItems = async (customer) => {
    const response = await fetch(
      `${local_signage}/api/v1/fetchrebateschemerates?RebateSchemeType=CUST&AreaId=${sfAreaId}&CustomerID=${customer.CustomerId}`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    const data = response.ok ? await response.json() : [];
    return (Array.isArray(data) ? data : []).map(item => ({ ...item, CustomerName: customer.CustomerName }));
  };

  // Fetches rate line-items for whichever customer(s) are selected and shows
  // them directly in the main table — no separate popup.
  const fetchRateDetailsForSelection = async (customerId) => {
    if (!customerId) {
      setRateDetailsRows([]);
      return;
    }

    setLoadingRateDetails(true);
    try {
      if (customerId === 'ALL') {
        const rows = [];
        for (const customer of customerList) {
          rows.push(...(await fetchOneCustomerRateItems(customer)));
        }
        setRateDetailsRows(rows);
      } else {
        const customer = customerList.find(c => c.CustomerId === customerId);
        if (!customer) {
          setRateDetailsRows([]);
          return;
        }
        setRateDetailsRows(await fetchOneCustomerRateItems(customer));
      }
      setHasLoadedRateDetails(true);
    } catch (err) {
      console.error('Error fetching rate details:', err);
      setRateDetailsRows([]);
    } finally {
      setLoadingRateDetails(false);
    }
  };

  // Just records the selection — fetching now happens when "Load Report"
  // is clicked, same as the area-wide report.
  const handleCustomerChange = (customerId) => {
    setSelectedCustomerId(customerId);
    setRateDetailsRows([]);
    setHasLoadedRateDetails(false);
  };

  const selectedCustomerData = selectedCustomerId && selectedCustomerId !== 'ALL'
    ? (customerList.find(c => c.CustomerId === selectedCustomerId) || null)
    : null;
  const isAllCustomersSelected = selectedCustomerId === 'ALL';
  const customerRowsToShow = isAllCustomersSelected ? customerList : (selectedCustomerData ? [selectedCustomerData] : []);

  const buildCustomerRateRows = async (customer) => {
    const response = await fetch(
      `${local_signage}/api/v1/fetchrebateschemerates?RebateSchemeType=CUST&AreaId=${sfAreaId}&CustomerID=${customer.CustomerId}`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    const data = response.ok ? await response.json() : [];

    return (Array.isArray(data) && data.length > 0 ? data : [{}]).map(item => ({
      'Customer ID': customer.CustomerId || '-',
      'Mobile': customer.Mobile || '-',
      'Customer Name': customer.CustomerName || '-',
      'Scheme ID': customer.RebateSchemeId || '-',
      'Scheme Name': customer.RebateSchemeName || '-',
      'Item Code': item.ItemCode || '-',
      'Item Name': item.ItemName || '-',
      'Qualify Qty': item.QualifyQty ?? '-',
      'Rate': item.Rate ?? '-',
      'Valid Till': formatYyyymmdd(item.validtill || item.ValidTill),
    }));
  };

  const exportCustomerRateToExcel = async () => {
    if (customerRowsToShow.length === 0) return;

    setIsExportingCustomerRate(true);
    try {
      const rows = [];
      for (const customer of customerRowsToShow) {
        rows.push(...(await buildCustomerRateRows(customer)));
      }

      const worksheet = XLSX.utils.json_to_sheet(rows);
      worksheet['!cols'] = [
        { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 12 }, { wch: 25 },
        { wch: 14 }, { wch: 20 }, { wch: 12 }, { wch: 10 }, { wch: 12 },
      ];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Customer Rate');
      const fileLabel = isAllCustomersSelected ? 'All_Customers' : `Customer_${selectedCustomerData.CustomerId}`;
      XLSX.writeFile(workbook, `${fileLabel}_Rates_${formatDateForApi(new Date())}.xlsx`);
    } catch (err) {
      console.error('Error exporting customer rate:', err);
      setError('Failed to export customer rate data.');
    } finally {
      setIsExportingCustomerRate(false);
    }
  };

  // Fetch SF Areas when component mounts
  useEffect(() => {
    const fetchAreas = async () => {
      if (!token || !userId) {
        handleAuthError();
        return;
      }

      setLoading(true);
      
      try {
        const response = await fetch(`${local_url}/api/SFArea/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            handleAuthError();
            return;
          }
          throw new Error('Failed to fetch SF Areas');
        }

        const data = await response.json();
        setSfAreas(data || []);
        
        // Auto-select first area if available
        if (data && data.length > 0) {
          setSfAreaId(data[0].sfAreaId);
        }
      } catch (error) {
        console.error('Error fetching SF Areas:', error);
        setError("Failed to fetch SF Areas. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (token && userId) {
      fetchAreas();
    } else {
      handleAuthError();
    }
  }, [token, userId]);

  // "Load Report" click handler — in Customer Rate mode this loads the
  // selected customer's (or ALL customers') rate details; otherwise it
  // loads the area-wide item report.
  const fetchReportData = async () => {
    if (!sfAreaId) {
      setError("Please select a Sales Force Area.");
      return;
    }

    if (showCustomerRate) {
      if (!selectedCustomerId) {
        setError("Please select a Customer.");
        return;
      }
      await fetchRateDetailsForSelection(selectedCustomerId);
      return;
    }

    await fetchAreaWideReport();
  };

  // Builds the area-wide report. fetchrebateschemerates with
  // RebateSchemeType=AREA returns area-level data that doesn't actually
  // vary by CustomerID, so it only needs to be called once per area (using
  // any one customer in that area to satisfy the required param) rather
  // than once per customer. Replaces the old QualifyRateReport API
  // entirely.
  const fetchAreaWideReport = async () => {
    setLoading(true);
    setError(null);

    try {
      const customersResponse = await fetch(
        `${local_signage}/api/v1/fetchrebateschemecustomerbyarea?AreaId=${sfAreaId}&PricingSchemeType=HORECA`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!customersResponse.ok) {
        if (customersResponse.status === 401) {
          handleAuthError();
          return;
        }
        throw new Error(`Server responded with status: ${customersResponse.status}`);
      }

      const customers = await customersResponse.json();
      const firstCustomer = Array.isArray(customers) && customers.length > 0 ? customers[0] : null;

      if (!firstCustomer) {
        setReportData([]);
        setError("No customers found for the selected area.");
        return;
      }

      const itemsResponse = await fetch(
        `${local_signage}/api/v1/fetchrebateschemerates?RebateSchemeType=AREA&AreaId=${sfAreaId}&CustomerID=${firstCustomer.CustomerId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const items = itemsResponse.ok ? await itemsResponse.json() : [];

      const rows = (Array.isArray(items) ? items : []).map(item => ({
        areaName: item.RebateSchemeName || '',
        itemCode: item.ItemCode || '',
        itemName: item.ItemName || '',
        qualifyQty: item.QualifyQty,
        rate: item.Rate,
        validTill: item.validtill || item.ValidTill,
      }));

      setReportData(rows);
      if (rows.length === 0) {
        setError("No data available for the selected filters.");
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      setError(`Failed to fetch report data: ${error.message}`);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on filter text
  const filteredData = reportData.filter(item => {
    if (!filterText) return true;
    
    const searchText = filterText.toLowerCase();
    return (
      (item.itemCode && item.itemCode.toLowerCase().includes(searchText)) ||
      (item.itemName && item.itemName.toLowerCase().includes(searchText))
    );
  });

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      setError("No data to export.");
      return;
    }
    
    try {
      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      
      // Create headers row
      const headers = ["Sr. No.", "AreaName", "ItemCode", "ItemName", "Qualify Qty", "Rate", "Valid Till"];

      // Create metadata rows
      const metadata = [
        [`Business Date: ${formatDisplayDate(businessDate)}`],
        [`SF Area: ${sfAreas.find(area => area.sfAreaId === sfAreaId)?.sfAreaName || sfAreaId}`],
        [`Channel: ${channel}`],
        [""] // Empty row for spacing
      ];

      // Convert the data to the format expected by xlsx
      const excelData = [
        ...metadata,
        headers,
        ...filteredData.map((item, index) => [
          index + 1,
          item.areaName || '',
          item.itemCode || '',
          item.itemName || '',
          typeof item.qualifyQty === 'number' ? item.qualifyQty : 0,
          typeof item.rate === 'number' ? item.rate : 0,
          formatYyyymmdd(item.validTill),
        ])
      ];

      // Create worksheet from data
      const worksheet = XLSX.utils.aoa_to_sheet(excelData);

      // Set column widths
      const columnWidths = [
        { wch: 8 },   // Sr. No.
        { wch: 25 },  // AreaName
        { wch: 12 },  // ItemCode
        { wch: 30 },  // ItemName
        { wch: 12 },  // Qualify Qty
        { wch: 12 },  // Rate
        { wch: 12 },  // Valid Till
      ];

      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "HORECA Qualify Rate Report");

      // Format date in filename using DDMMYYYY format
      const dateFormatted = formatDateForApi(businessDate);
      const sfAreaName = sfAreas.find(area => area.sfAreaId === sfAreaId)?.sfAreaName || sfAreaId;

      // Create filename
      const fileName = `horeca_qualify_rate_${sfAreaName}_${dateFormatted}.xlsx`;
      
      // Generate and save the file
      XLSX.writeFile(workbook, fileName);
      
    } catch (error) {
      console.error("Error exporting Excel file:", error);
      setError("Failed to export Excel file.");
    }
  };

  // Enhanced Loader component
  const Loader = () => (
    <div className="horecaQualify-loader-container">
      <div className="horecaQualify-loader">
        <div className="horecaQualify-loader-circle"></div>
        <div className="horecaQualify-loader-circle"></div>
        <div className="horecaQualify-loader-circle"></div>
      </div>
      <p className="horecaQualify-loader-text">Loading data...</p>
    </div>
  );

  return (
    <>
      <Header />
      <div className="horecaQualify-container">
        {loading && <Loader />}

        <div className="horecaQualify-controls">
          {/* SF Area Dropdown */}
          <FormControl variant="outlined" className="horecaQualify-formControl" style={{ minWidth: 180 }} required>
            <InputLabel id="sfarea-select-label">Area</InputLabel>
            <Select
              labelId="sfarea-select-label"
              value={sfAreaId}
              onChange={(e) => handleAreaChange(e.target.value)}
              label="Sales Force Area"
              disabled={loading}
            >
              {sfAreas.map((area) => (
                <MenuItem key={area.sfAreaId} value={area.sfAreaId}>
                  {area.sfAreaName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Customer Rate toggle - replaces the old fixed "Channel" dropdown */}
          <FormControlLabel
            control={
              <Checkbox
                checked={showCustomerRate}
                onChange={(e) => handleCustomerRateToggle(e.target.checked)}
              />
            }
            label="Customer Rate"
            style={{ marginLeft: 0 }}
          />

          {/* Customer Dropdown - only shown once "Customer Rate" is checked */}
          {showCustomerRate && (
            <FormControl variant="outlined" className="horecaQualify-formControl" style={{ minWidth: 200 }} required>
              <InputLabel id="customer-select-label">Select Customer</InputLabel>
              <Select
                labelId="customer-select-label"
                value={selectedCustomerId}
                onChange={(e) => handleCustomerChange(e.target.value)}
                label="Select Customer"
                disabled={loadingCustomerList || !sfAreaId}
              >
                <MenuItem value="ALL">ALL</MenuItem>
                {customerList.map((customer) => (
                  <MenuItem key={customer.CustomerId} value={customer.CustomerId}>
                    {customer.CustomerName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Date Picker */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Business Date"
              value={dayjs(businessDate)}
              onChange={handleDateChange}
              format="DD-MM-YYYY"
              disabled
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: 150,
                    backgroundColor: '#F1F8E9',
                    '& .MuiOutlinedInput-root': {
                      height: 40,
                    }
                  },
                },
              }}
            />
          </LocalizationProvider>

          {/* Search Field */}
          <div className="horecaQualify-search">
            <input
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="Search items..."
              className="horecaQualify-search-field"
              disabled={loading}
            />
          </div>

          {/* Buttons */}
          <div className="horecaQualify-buttons">
            <button
              onClick={fetchReportData}
              className="horecaQualify-button horecaQualify-load-button"
              disabled={loading || loadingRateDetails || !sfAreaId || (showCustomerRate && !selectedCustomerId)}
            >
              {loadingRateDetails ? 'Loading...' : 'Load Report'}
            </button>

            <button
              onClick={showCustomerRate ? exportCustomerRateToExcel : exportToExcel}
              className="horecaQualify-button horecaQualify-export-button"
              disabled={showCustomerRate
                ? (isExportingCustomerRate || customerRowsToShow.length === 0)
                : (loading || filteredData.length === 0)}
            >
              {showCustomerRate
                ? (isExportingCustomerRate ? 'Exporting...' : 'Export to Excel')
                : 'Export to Excel'}
            </button>
          </div>
        </div>

        {error && <div className="horecaQualify-error-message">{error}</div>}

        {showCustomerRate ? (
          <div className="horecaQualify-table-container">
            <table className="horecaQualify-report">
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Customer Name</th>
                  <th>Item Code</th>
                  <th>Item Name</th>
                  <th>Qualify Qty</th>
                  <th>Rate</th>
                  <th>Valid Till</th>
                </tr>
              </thead>
              <tbody>
                {loadingRateDetails ? (
                  <tr>
                    <td colSpan={7} className="horecaQualify-no-data">Loading rate details...</td>
                  </tr>
                ) : rateDetailsRows.length > 0 ? (
                  rateDetailsRows.map((item, index) => (
                    <tr key={index} className={index % 2 === 0 ? "horecaQualify-row-even" : "horecaQualify-row-odd"}>
                      <td>{index + 1}</td>
                      <td>{item.CustomerName || '-'}</td>
                      <td>{item.ItemCode || '-'}</td>
                      <td>{item.ItemName || '-'}</td>
                      <td>{item.QualifyQty ?? '-'}</td>
                      <td>{item.Rate ?? '-'}</td>
                      <td>{formatYyyymmdd(item.validtill || item.ValidTill)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="horecaQualify-no-data">
                      {!sfAreaId
                        ? 'Select an area first'
                        : !selectedCustomerId
                          ? 'Select a customer to view rate details'
                          : !hasLoadedRateDetails
                            ? "Click 'Load Report' to view data"
                            : 'No rate details found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="horecaQualify-table-container">
            <table className="horecaQualify-report">
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>AreaName</th>
                  <th>ItemCode</th>
                  <th>ItemName</th>
                  <th>Qualify Qty</th>
                  <th>Rate</th>
                  <th>Valid Till</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((item, index) => (
                    <tr key={index} className={index % 2 === 0 ? "horecaQualify-row-even" : "horecaQualify-row-odd"}>
                      <td>{index + 1}</td>
                      <td>{item.areaName || '-'}</td>
                      <td>{item.itemCode || '-'}</td>
                      <td>{item.itemName || '-'}</td>
                      <td className="horecaQualify-number">
                        {typeof item.qualifyQty === 'number'
                          ? item.qualifyQty.toLocaleString('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
                          : '0.000'}
                      </td>
                      <td className="horecaQualify-currency">
                        ₹{typeof item.rate === 'number'
                          ? item.rate.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : '0.00'}
                      </td>
                      <td>{formatYyyymmdd(item.validTill)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="horecaQualify-no-data">
                      {reportData.length === 0 && !error
                        ? "Click 'Load Report' to view data"
                        : reportData.length === 0 && error
                          ? "No data available"
                          : "No data matches your search criteria"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

HorecaDayRate.propTypes = {
  // No specific props needed for this component
};

export default HorecaDayRate;